import styled from "styled-components";

import { media } from "../../styles/media";

export const SideMenuStyles = styled.div`
  height: 100vh;
  width: 240px;
  position: fixed;
  top: 0;
  z-index: 999;
  user-select: none;

  /* Below the tablet breakpoint the sidebar becomes an off-canvas drawer that
     slides in from the left; desktop behaviour above the breakpoint is intact. */
  ${({ theme, $isOpen }) => media.maxTablet`
    left: 0;
    z-index: ${theme.zIndex.drawer};
    transform: translateX(${$isOpen ? "0" : "-100%"});
    transition: transform 0.32s cubic-bezier(0.4, 0, 0.2, 1);
  `}

  .sideMenu {
    width: 100%;
    height: 100%;
    background: ${({ theme }) => theme.colors.semantic.container.white};
    box-shadow: ${({ theme }) => theme.shadows.card};
    border-right: 1px solid ${({ theme }) => theme.colors.semantic.separator};
    padding: 42px 0 25px 0;
    position: relative;
    z-index: 2;

    .logo {
      margin: 0 auto;
      display: block;
      color: ${({ theme }) => theme.colors.primary[900]};
    }

    .brachContainer {
      width: calc(100% - 24px);
      margin: 35px 12px 0;
      padding: ${({ theme }) => theme.spacing[12]} ${({ theme }) => theme.spacing[16]};
      cursor: pointer;
      min-height: 49px;
      display: flex;
      flex-direction: column;
      justify-content: center;
      border-radius: 8px;
      transition: background 0.2s ease;

      &:hover {
        background: ${({ theme }) => theme.colors.accent[50]};
      }

      .selectBranch {
        font-style: normal;
        font-weight: ${({ theme }) => theme.typography.body.b3.weight};
        font-size: ${({ theme }) => theme.typography.body.b3.size};
        line-height: ${({ theme }) => theme.typography.body.b3.lineHeight};
        color: ${({ theme }) => theme.colors.semantic.text.weak};
      }

      .chooseBracnhContainer {
        display: flex;
        align-items: center;
        justify-content: space-between;

        .chosenBranch {
          font-style: normal;
          font-weight: 600;
          font-size: ${({ theme }) => theme.typography.body.b2.size};
          line-height: 17px;
          letter-spacing: 0.1px;
          color: ${({ theme }) => theme.colors.accent[500]};
        }

        .arrowContainer {
          display: flex;
          align-items: center;
          transition: transform 0.3s cubic-bezier(0.4, 0, 0.2, 1);
        }

        .arrowAnim {
          transform: rotate(180deg);
        }
      }

      .address {
        font-style: normal;
        font-weight: ${({ theme }) => theme.typography.title.t2.weight};
        font-size: 10px;
        line-height: ${({ theme }) => theme.typography.body.b3.lineHeight};
        letter-spacing: 0.1px;
        color: ${({ theme }) => theme.colors.semantic.text.weak};
        width: 100%;
        white-space: nowrap;
        overflow: hidden;
        text-overflow: ellipsis;
      }
    }

    .linksContainer {
      margin-top: 38px;
    }

    .notifSection {
      position: absolute;
      bottom: 84px;
      left: 12px;
      right: 12px;
      padding-top: 10px;
      border-top: 1px solid #f1f1f5;

      /* In the mobile drawer the bell lives in the top bar instead. */
      ${media.maxTablet`
        display: none;
      `}

      .notifRow {
        display: flex;
        align-items: center;
        gap: 14px;
        padding: 12px;
        border-radius: 8px;
        cursor: pointer;
        color: ${({ theme }) => theme.colors.primary[600]};
        transition: background 0.15s ease, color 0.15s ease;

        &:hover {
          background: ${({ theme }) => theme.colors.accent[50]};
          color: ${({ theme }) => theme.colors.semantic.text.strong};
        }

        &.isActive {
          background: ${({ theme }) => theme.colors.accent[50]};
          color: ${({ theme }) => theme.colors.accent[500]};
        }

        .notifLabel {
          flex: 1;
          font-size: ${({ theme }) => theme.typography.body.b3.size};
          font-weight: 600;
          line-height: 20px;
        }
      }
    }

    .profile {
      position: absolute;
      bottom: 20px;
      left: 12px;
      right: 12px;
      display: flex;
      align-items: center;
      padding: 10px 12px;
      cursor: pointer;
      border-radius: 8px;
      transition: background 0.15s ease;

      &:hover {
        background: ${({ theme }) => theme.colors.accent[50]};
      }

      &.isActive {
        background: ${({ theme }) => theme.colors.accent[50]};
      }

      .imgContainer {
        margin-right: 9px;
        display: flex;
        align-items: center;
        justify-content: center;

        img {
          width: 36px;
          height: 36px;
          border-radius: 50%;
          object-fit: cover;
        }
      }

      .profileSettings {
        .userName {
          font-style: normal;
          font-weight: bold;
          font-size: ${({ theme }) => theme.typography.body.b3.size};
          line-height: 14px;
          color: ${({ theme }) => theme.colors.semantic.text.strong};
        }

        .accSettings {
          font-style: normal;
          font-weight: bold;
          font-size: 10px;
          line-height: 14px;
          color: ${({ theme }) => theme.colors.semantic.text.weak};
        }
      }

      &.isActive .userName {
        color: ${({ theme }) => theme.colors.accent[500]};
      }

      &.isActive .accSettings {
        color: ${({ theme }) => theme.colors.accent[500]};
        opacity: 0.75;
      }
    }
  }

  .branchesContainer {
    width: calc(100vw - 240px);
    height: 100%;
    background: transparent;
    position: absolute;
    top: 0;
    margin-left: 240px;
    pointer-events: none;

    /* On mobile the branches panel covers the whole drawer (full drawer width)
       and slides over the nav; the drawer's own backdrop dims the rest.
       z-index must beat .sideMenu (z-index: 2) or the panel hides behind the
       nav since they now overlap. */
    ${media.maxTablet`
      width: 100%;
      margin-left: 0;
      z-index: 3;
    `}
    visibility: hidden;
    transition: background 0.35s cubic-bezier(0.4, 0, 0.2, 1),
                visibility 0s linear 0.35s;

    &.branchesOpen {
      background: ${({ theme }) => theme.colors.primary[900]}33;
      pointer-events: auto;
      visibility: visible;
      transition: background 0.35s cubic-bezier(0.4, 0, 0.2, 1),
                  visibility 0s linear 0s;
    }

    .branchesCard {
      height: 100%;
      width: 337px;
      /* Cover the full drawer on mobile, square edges (it sits flush in the
         drawer rather than floating). */
      ${media.maxTablet`
        width: 100%;
        border-radius: 0;
      `}
      position: absolute;
      left: 0;
      transform: translateX(-337px);
      opacity: 0;
      border-radius: 0 ${({ theme }) => theme.radius[16]} ${({ theme }) => theme.radius[16]} 0;
      overflow: hidden;
      transition: transform 0.35s cubic-bezier(0.4, 0, 0.2, 1),
                  opacity 0.3s cubic-bezier(0.4, 0, 0.2, 1);

      .branchesHeader {
        display: flex;
        align-items: center;
        justify-content: space-between;
      }

      .branchesBody {
        padding: 0;
        position: relative;
        z-index: 2;
        /* Scroll the branch list inside the panel if it's long (header stays). */
        ${media.maxTablet`
          max-height: calc(100vh - 80px);
          overflow-y: auto;
        `}
      }
    }

    &.branchesOpen .branchesCard {
      transform: translateX(0);
      opacity: 1;
    }
  }
`;
